import React, { useState, useEffect, useRef } from 'react'
import GoldSiverHeader from '../../HomePage/GoldSiverHeader'
import { Badge, Col, Modal, Row, Table } from 'react-bootstrap'
import axiosInstance from '../../../api/axios'
import { API_ENDPOINTS } from '../../../api/endpoint'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'

function Pokedex() {
  const { t } = useTranslation()
  const [allPokemons, setAllPokemons] = useState([])
  const [pokedexStats, setPokedexStats] = useState({
    total_pokemon: 0,
    seen: { count: 0, percentage: 0 },
    battled: { count: 0, percentage: 0 },
    caught: { count: 0, percentage: 0 }
  })
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [selectedPokemon, setSelectedPokemon] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [seenIds, setSeenIds] = useState([])
  const [caughtIds, setCaughtIds] = useState([])
  const scrollContainerRef = useRef(null)

  // Load pokemons with pagination
  const loadPokemons = async (page = 1, append = false) => {
    try {
      if (append) {
        setLoadingMore(true)
      } else {
        setLoading(true)
      }
      
      const pokemonsResponse = await axiosInstance.get(API_ENDPOINTS.GET_ALL_POKEMONS_FOR_POKEDEX, {
        params: {
          page: page,
          limit: 50 // Load 50 pokemons at a time
        }
      })
      
      if (pokemonsResponse.data && pokemonsResponse.data.success && Array.isArray(pokemonsResponse.data.data)) {
        if (append) {
          setAllPokemons(prev => [...prev, ...pokemonsResponse.data.data])
        } else {
          setAllPokemons(pokemonsResponse.data.data)
        }
        
        if (pokemonsResponse.data.pagination) {
          setHasMore(pokemonsResponse.data.pagination.hasMore)
          setCurrentPage(page)
        }
        
        console.log(`Pokemons loaded (page ${page}):`, pokemonsResponse.data.data.length)
      } else if (Array.isArray(pokemonsResponse.data)) {
        // Fallback for old API format
        if (append) {
          setAllPokemons(prev => [...prev, ...pokemonsResponse.data])
        } else {
          setAllPokemons(pokemonsResponse.data)
        }
        setHasMore(false)
      } else {
        console.error('Invalid pokemons response:', pokemonsResponse.data)
      }
    } catch (error) {
      console.error('Error fetching pokemons:', error)
      if (!append) {
        toast.error('Failed to load pokemons')
      }
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        
        // Fetch pokedex stats
        const statsResponse = await axiosInstance.get(API_ENDPOINTS.GET_POKEDEX_STATS)
        if (statsResponse.data && statsResponse.data.success) {
          setPokedexStats(statsResponse.data.data)
          // Get seen and caught IDs
          const seen = statsResponse.data.data.seen?.ids || []
          const caught = statsResponse.data.data.caught?.ids || []
          setSeenIds(seen)
          setCaughtIds(caught)
          console.log('Pokedex stats loaded:', {
            seenCount: seen.length,
            caughtCount: caught.length,
            caughtIds: caught
          })
        }
        
        // Load first page of pokemons
        await loadPokemons(1, false)
      } catch (error) {
        console.error('Error fetching pokedex data:', error)
        toast.error('Failed to load pokedex data')
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  // Infinite scroll handler
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    if (!scrollContainer) return

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer
      // Load more when user is 200px from bottom
      if (scrollHeight - scrollTop - clientHeight < 200 && hasMore && !loadingMore && !loading) {
        loadPokemons(currentPage + 1, true)
      }
    }

    scrollContainer.addEventListener('scroll', handleScroll)
    return () => scrollContainer.removeEventListener('scroll', handleScroll)
  }, [hasMore, loadingMore, loading, currentPage])

  const handlePokemonClick = (pokemon) => {
    setSelectedPokemon(pokemon)
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setSelectedPokemon(null)
  }

  // Check if pokemon is seen or caught
  const isPokemonSeen = (wildId) => {
    const id = parseInt(wildId)
    return seenIds.includes(id)
  }

  const isPokemonCaught = (wildId) => {
    const id = parseInt(wildId)
    const result = caughtIds.includes(id)
    // Debug log for caught check
    if (Math.random() < 0.01) { // 1% chance to log
      console.log('Checking if caught:', { wildId, id, caughtIds, result })
    }
    return result
  }

  return (
    <div>
      <GoldSiverHeader previous={'/home'} title='Pokedex'>
        <style>{`
          @media (min-width: 576px) {
            .arPokedex_area_section .col-sm-4 {
              flex: 0 0 auto;
              width: 20% !important;
            }
          }
        `}</style>
        <section className="arPokedex_area_section">
          <div className="container">
            <div className="row">
              <div className="col-md-12">
                <div className="ar_pokedex_area">
                  {/* Left side - Statistics with percentages */}
                  <div className="ar_pokedex_area_left">
                    <h4>{t('Collected')}:</h4>
                    <h4>{loading ? '...' : `${pokedexStats.caught.count} ${t('out of')} ${pokedexStats.total_pokemon} ${t('Pokemon')}`}</h4>
                    
                    {/* Percentage display for caught */}
                    <div className="ar_pokedexLeft_parcent">
                      <div style={{ position: 'relative', width: '100px', textAlign: 'center', margin: '0 auto' }}>
                        <img src="/images/pokedex/parcebtage.png" alt="" style={{ width: '100%' }} />
                        <div style={{ 
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          color: 'rgb(0, 0, 0)',
                          background: '#ffffff',
                          borderRadius: '1000px',
                          height: '55px',
                          width: '55px',
                          alignContent: 'center',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {loading ? '...' : `${pokedexStats.caught.percentage}%`}
                        </div>
                      </div>
                    </div>
                    
                    {/* Statistics buttons - 3 buttons for seen, battled, caught */}
                    <div className="ar_pokedex_prcent_btn">
                      <div style={{ 
                        textAlign: 'center', 
                        marginBottom: '15px',
                        padding: '15px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: '10px',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
                        minWidth: '140px',
                        cursor: 'pointer',
                        transition: 'transform 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                      >
                        <div style={{ 
                          color: '#fff',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                          width: '100%',
                          textAlign: 'center'
                        }}>
                          <div>{t('Met')}</div>
                          <div style={{ fontSize: '20px', marginTop: '5px', fontWeight: 'bold' }}>
                            {loading ? '...' : `${pokedexStats.seen.count}`}
                          </div>
                          <div style={{ fontSize: '12px', marginTop: '5px', opacity: 0.9 }}>
                            {loading ? '' : `${pokedexStats.seen.percentage}%`}
                          </div>
                        </div>
                      </div>
                      <div style={{ 
                        textAlign: 'center', 
                        marginBottom: '15px',
                        padding: '15px',
                        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                        borderRadius: '10px',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
                        minWidth: '140px',
                        cursor: 'pointer',
                        transition: 'transform 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                      >
                        <div style={{ 
                          color: '#fff',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                          width: '100%',
                          textAlign: 'center'
                        }}>
                          <div>{t('Battled')}</div>
                          <div style={{ fontSize: '20px', marginTop: '5px', fontWeight: 'bold' }}>
                            {loading ? '...' : `${pokedexStats.battled.count}`}
                          </div>
                          <div style={{ fontSize: '12px', marginTop: '5px', opacity: 0.9 }}>
                            {loading ? '' : `${pokedexStats.battled.percentage}%`}
                          </div>
                        </div>
                      </div>
                      <div style={{ 
                        textAlign: 'center', 
                        marginBottom: '15px',
                        padding: '15px',
                        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                        borderRadius: '10px',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
                        minWidth: '140px',
                        cursor: 'pointer',
                        transition: 'transform 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                      >
                        <div style={{ 
                          color: '#fff',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                          width: '100%',
                          textAlign: 'center'
                        }}>
                          <div>{t('Caught')}</div>
                          <div style={{ fontSize: '20px', marginTop: '5px', fontWeight: 'bold' }}>
                            {loading ? '...' : `${pokedexStats.caught.count}`}
                          </div>
                          <div style={{ fontSize: '12px', marginTop: '5px', opacity: 0.9 }}>
                            {loading ? '' : `${pokedexStats.caught.percentage}%`}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Right side - All Pokemon grid */}
                  <div className="ar_pokedex_area_right">
                    <div 
                      ref={scrollContainerRef}
                      className="arPokedex_areaScrool" 
                      style={{ maxHeight: '600px', overflowY: 'auto' }}
                    >
                      <Row className="g-2">
                        {loading && allPokemons.length === 0 ? (
                          <Col md={12} className="text-center text-white">
                            <p>{t('Loading Pokemon...')}</p>
                          </Col>
                        ) : allPokemons.length === 0 ? (
                          <Col md={12} className="text-center text-white">
                            <p>{t('No Pokemon Found')}</p>
                          </Col>
                        ) : (
                          <>
                            {allPokemons.map((pokemon) => {
                            const isSeen = isPokemonSeen(pokemon.wild_id)
                            const isCaught = isPokemonCaught(pokemon.wild_id)
                            
                            return (
                              <Col 
                                key={pokemon.wild_id} 
                                sm={4} 
                                className="character-item2-inner2 gap-2 cursor-pointer"
                                onClick={() => handlePokemonClick(pokemon)}
                              >
                                <div
                                  className={`character-item2-inner3 pokemon-gifs ${pokemon.type1 ? pokemon.type1.toLowerCase() : ''} ${pokemon.type2 ? pokemon.type2.toLowerCase() : ''}`}
                                  style={{ 
                                    opacity: isCaught ? 1 : 0.3,
                                    filter: isCaught ? 'none' : 'grayscale(100%)'
                                  }}
                                >
                                  <div className="inner-cut-bg">
                                    <img 
                                      src={`/images/pokemon/${pokemon.wild_id}.gif`} 
                                      alt={pokemon.naam}
                                      onError={(e) => {
                                        e.target.src = '/images/pokemon/1.gif' // Fallback image
                                      }}
                                    />
                                    <div className="character-item2-inner5 b--35">
                                      <p>
                                        {pokemon.naam}
                                        {isCaught && <span style={{ color: '#58ff31', marginLeft: '5px' }}>✓</span>}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </Col>
                            )
                          })}
                          {loadingMore && (
                            <Col md={12} className="text-center text-white" style={{ padding: '20px' }}>
                              <p>{t('Loading more Pokemon...')}</p>
                            </Col>
                          )}
                          {!hasMore && allPokemons.length > 0 && (
                            <Col md={12} className="text-center text-white" style={{ padding: '20px' }}>
                              <p>{t('All Pokemon loaded')}</p>
                            </Col>
                          )}
                          </>
                        )}
                      </Row>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Modal for Pokemon details */}
        <Modal 
          show={showModal} 
          onHide={handleCloseModal}
          size="lg"
          centered
          className="pokemon-modal"
        >
          <Modal.Header closeButton className="bg-theme text-white">
            <Modal.Title>
              {selectedPokemon && `#${selectedPokemon.wild_id} ${selectedPokemon.naam}`}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="bg-theme text-white">
            {selectedPokemon && (
              <Row>
                <Col md={6} className="text-center">
                  <img 
                    src={`/images/pokemon/${selectedPokemon.wild_id}.gif`} 
                    alt={selectedPokemon.naam}
                    style={{ maxWidth: '200px' }}
                  />
                  <div className="mt-3">
                    <Badge bg="warning" className="poke-profile-badges me-2">
                      {selectedPokemon.type1}
                    </Badge>
                    {selectedPokemon.type2 && (
                      <Badge bg="danger" className="poke-profile-badges">
                        {selectedPokemon.type2}
                      </Badge>
                    )}
                  </div>
                </Col>
                <Col md={6}>
                  <Table className="table-theme text-white">
                    <tbody>
                      <tr>
                        <td><b>» Wild ID:</b></td>
                        <td className="text-center">{selectedPokemon.wild_id}</td>
                      </tr>
                      <tr>
                        <td><b>» Name:</b></td>
                        <td className="text-center">{selectedPokemon.naam}</td>
                      </tr>
                      <tr>
                        <td><b>» Region:</b></td>
                        <td className="text-center">{selectedPokemon.wereld || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td><b>» Area:</b></td>
                        <td className="text-center">{selectedPokemon.gebied || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td><b>» Rarity:</b></td>
                        <td className="text-center">{selectedPokemon.zeldzaamheid || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td><b>» Growth:</b></td>
                        <td className="text-center">{selectedPokemon.groei || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td><b>» Base Stats:</b></td>
                        <td className="text-center">
                          HP: {selectedPokemon.hp_base || 0} | 
                          ATK: {selectedPokemon.attack_base || 0} | 
                          DEF: {selectedPokemon.defence_base || 0}
                        </td>
                      </tr>
                      <tr>
                        <td><b>» Attacks:</b></td>
                        <td className="text-center">
                          {selectedPokemon.aanval_1 || '-'} | 
                          {selectedPokemon.aanval_2 || '-'} | 
                          {selectedPokemon.aanval_3 || '-'} | 
                          {selectedPokemon.aanval_4 || '-'}
                        </td>
                      </tr>
                      <tr>
                        <td><b>» Ability:</b></td>
                        <td className="text-center">{selectedPokemon.ability || 'N/A'}</td>
                      </tr>
                    </tbody>
                  </Table>
                </Col>
              </Row>
            )}
          </Modal.Body>
        </Modal>
      </GoldSiverHeader>
    </div>
  )
}

export default Pokedex
